import { Button } from "@l30/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Inbox } from "lucide-react";
import { useMemo } from "react";

import { useIdeationMaps, useOpenExtractionsList } from "../../api/ideation-maps.js";
import { Sheet } from "../Sheet.js";

import { useExtractionReview } from "./extraction-review-context.js";
import { extractionSourceLabel, proposalSummary } from "./extraction-review-helpers.js";
import { ExtractionReviewBody } from "./ExtractionReviewBody.js";

import type { ExtractionView } from "@l30/shared";

function groupByMap(
  extractions: ExtractionView[],
  titleById: Map<string, string>,
): Array<{ mapId: string | null; mapTitle: string; rows: ExtractionView[] }> {
  const groups = new Map<string | null, ExtractionView[]>();
  for (const ex of extractions) {
    const key = ex.mapId;
    const list = groups.get(key) ?? [];
    list.push(ex);
    groups.set(key, list);
  }
  return [...groups.entries()].map(([mapId, rows]) => ({
    mapId,
    mapTitle: mapId ? (titleById.get(mapId) ?? "Untitled map") : "No target map",
    rows,
  }));
}

function InboxStep({
  groups,
  onReview,
  onGoToMap,
}: {
  groups: Array<{ mapId: string | null; mapTitle: string; rows: ExtractionView[] }>;
  onReview: (id: string) => void;
  onGoToMap: (mapId: string) => void;
}) {
  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <Inbox className="h-8 w-8 text-fg-muted" aria-hidden="true" />
        <p className="text-sm text-fg-muted">Nothing waiting</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.mapId ?? "none"} className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-fg">
              {group.mapTitle}
              <span className="ml-2 font-normal text-fg-muted">({group.rows.length})</span>
            </h3>
            {group.mapId ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onGoToMap(group.mapId!)}
              >
                Open map
              </Button>
            ) : null}
          </div>
          <ul className="divide-y rounded-md border border-border">
            {group.rows.map((ex) => {
              const pendingItems = ex.items.filter((i) => i.status === "pending").length;
              return (
                <li key={ex.id} className="flex items-center justify-between gap-3 px-3 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-fg">
                      {extractionSourceLabel(ex.sourceKind)}
                    </p>
                    <p className="text-xs text-fg-muted">
                      {ex.status === "processing"
                        ? "Extracting…"
                        : ex.status === "failed"
                          ? "Failed"
                          : proposalSummary(ex.items)}
                      {ex.status === "pending" && pendingItems === 0 && ex.items.length > 0
                        ? " · ready to Apply"
                        : null}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={ex.status === "processing"}
                    onClick={() => onReview(ex.id)}
                  >
                    Review
                  </Button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function ExtractionReviewDrawer() {
  const { isOpen, focusedExtractionId, closeReview, openReview, backToInbox } =
    useExtractionReview();
  const list = useOpenExtractionsList();
  const maps = useIdeationMaps();
  const navigate = useNavigate();

  const titleById = useMemo(() => {
    const m = new Map<string, string>();
    for (const map of maps.data?.maps ?? []) {
      m.set(map.id, map.title);
    }
    return m;
  }, [maps.data?.maps]);

  const groups = useMemo(
    () => groupByMap(list.data?.extractions ?? [], titleById),
    [list.data?.extractions, titleById],
  );

  const focused = (list.data?.extractions ?? []).find((e) => e.id === focusedExtractionId);
  const mapTitle = focused?.mapId
    ? (titleById.get(focused.mapId) ?? "Map")
    : focused
      ? "No target map"
      : "";

  const sheetTitle = focusedExtractionId ? mapTitle || "Review extraction" : "Pending extractions";

  return (
    <Sheet
      open={isOpen}
      onClose={closeReview}
      title={sheetTitle}
      className="max-w-lg"
      headerExtra={
        focusedExtractionId ? (
          <Button type="button" variant="ghost" size="sm" onClick={backToInbox}>
            <ArrowLeft className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            Back to inbox
          </Button>
        ) : null
      }
    >
      {focusedExtractionId ? (
        <div className="space-y-3">
          {focused ? (
            <p className="text-xs text-fg-muted">
              {extractionSourceLabel(focused.sourceKind)}
              {focused.mapId ? ` · ${mapTitle}` : ""}
            </p>
          ) : null}
          <ExtractionReviewBody
            extractionId={focusedExtractionId}
            persistDiscard
            onApplied={() => {
              const remaining =
                list.data?.extractions.filter((e) => e.id !== focusedExtractionId).length ?? 0;
              backToInbox();
              void list.refetch().then((result) => {
                if ((result.data?.extractions.length ?? remaining) === 0) {
                  closeReview();
                }
              });
            }}
            onDiscard={() => {
              backToInbox();
              void list.refetch();
            }}
          />
        </div>
      ) : (
        <InboxStep
          groups={groups}
          onReview={(id) => openReview({ extractionId: id })}
          onGoToMap={(mapId) => {
            void navigate({ to: "/ideation/maps/$mapId", params: { mapId } });
          }}
        />
      )}
    </Sheet>
  );
}
